/**
 * Maps browser locale / region to a dial-code.
 * Reusable across any component that needs a default country code.
 *
 * @returns The dial code string (e.g. "+91", "+1") based on the user's
 *          browser locale or timezone. Falls back to "+91" (India).
 */

const REGION_TO_DIAL: Record<string, string> = {
  IN: '+91',
  US: '+1',
  GB: '+44',
  AE: '+971',
  SA: '+966',
  AU: '+61',
  CA: '+1',
  SG: '+65',
  MY: '+60',
  DE: '+49',
  FR: '+33',
  JP: '+81',
  CN: '+86',
  BR: '+55',
  ZA: '+27',
  NZ: '+64',
  PH: '+63',
  BD: '+880',
  PK: '+92',
  LK: '+94',
  NP: '+977',
  KW: '+965',
  QA: '+974',
  BH: '+973',
  OM: '+968',
  EG: '+20',
  KE: '+254',
  NG: '+234',
  IT: '+39',
  ES: '+34',
  NL: '+31',
  SE: '+46',
  NO: '+47',
  DK: '+45',
  FI: '+358',
  IE: '+353',
  CH: '+41',
  AT: '+43',
  BE: '+32',
  PT: '+351',
  PL: '+48',
  RU: '+7',
  KR: '+82',
  TH: '+66',
  VN: '+84',
  ID: '+62',
  MX: '+52',
  AR: '+54',
  CL: '+56',
  CO: '+57',
  PE: '+51',
};

const TZ_PREFIX_TO_DIAL: Array<[string, string]> = [
  ['Asia/Kolkata', '+91'],
  ['Asia/Calcutta', '+91'],
  ['America/New_York', '+1'],
  ['America/Chicago', '+1'],
  ['America/Denver', '+1'],
  ['America/Los_Angeles', '+1'],
  ['Europe/London', '+44'],
  ['Asia/Dubai', '+971'],
  ['Asia/Riyadh', '+966'],
  ['Australia/', '+61'],
  ['Asia/Singapore', '+65'],
  ['Asia/Kuala_Lumpur', '+60'],
  ['Europe/Berlin', '+49'],
  ['Europe/Paris', '+33'],
  ['Asia/Tokyo', '+81'],
  ['Asia/Shanghai', '+86'],
  ['America/Sao_Paulo', '+55'],
  ['Africa/Johannesburg', '+27'],
];

const DEFAULT_FALLBACK = '+91';

export function getDefaultDialCode(fallback: string = DEFAULT_FALLBACK): string {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions();

    // 1. Infer from timezone first (more reliable than locale)
    const tz = resolved.timeZone;
    if (tz) {
      for (const [prefix, code] of TZ_PREFIX_TO_DIAL) {
        if (tz.startsWith(prefix)) return code;
      }
    }

    // 2. Extract region from locale string (e.g. "en-IN" → "IN")
    const locale = resolved.locale ?? navigator.language;
    const regionMatch = locale.match(/[-_]([A-Z]{2})$/i);
    if (regionMatch) {
      const region = regionMatch[1].toUpperCase();
      if (REGION_TO_DIAL[region]) return REGION_TO_DIAL[region];
    }
  } catch {
    // Intl not supported – fall through
  }

  return fallback;
}

export default getDefaultDialCode;
